import { CONTEXT_INVOICE_COMMISSION } from "./followupConstants";
import {
  buildCommissionHubEditInvoicePath,
  buildFinanceEditInvoicePath,
} from "./invoiceCreateHelpers";

const INVOICE_FOLLOWUP_TITLE_RE = /invoice follow-up/i;

function parseNotificationPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "object") return payload;
  if (typeof payload !== "string") return null;
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function numId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function isInvoiceFollowupNotification(notification, payload) {
  if (payload?.finance_invoice_id || payload?.context_type?.startsWith("invoice_")) {
    return true;
  }
  const title = `${notification?.title || ""} ${notification?.message || ""} ${notification?.notes || ""}`;
  return INVOICE_FOLLOWUP_TITLE_RE.test(title);
}

function resolveInvoiceFollowupRoute(notification, payload) {
  if (!isInvoiceFollowupNotification(notification, payload)) {
    return null;
  }

  const invoiceId =
    payload?.finance_invoice_id ||
    notification.applicationid ||
    notification.application_id;
  const clientId =
    payload?.client_id ||
    notification.clientid ||
    notification.client?.id;

  if (!invoiceId || !clientId) {
    return null;
  }

  const isCommission =
    payload?.context_type === CONTEXT_INVOICE_COMMISSION ||
    payload?.context_type === "invoice_commission";

  if (isCommission) {
    return buildCommissionHubEditInvoicePath(clientId, invoiceId, {
      moduleTab: "commission",
      statusTab: payload?.statusTab || "all",
    });
  }

  const tabs = {
    moduleTab: "application",
    statusTab: payload?.statusTab || "draft",
  };
  return buildFinanceEditInvoicePath(clientId, invoiceId, tabs);
}

/**
 * Mirrors legacy ViewNotification.js link rules for types without payload.route.
 */
function resolveLegacyNotificationRoute(notification) {
  const type = Number(notification?.type);
  const appType = Number(notification?.applicationtype);
  const appId = numId(notification?.applicationid);
  const clientId = numId(notification?.clientid || notification?.client?.id);

  if ([1, 8, 11].includes(type) && clientId) {
    return `/clients/view-client-detail/${clientId}`;
  }

  if (type === 6 && clientId && !isInvoiceFollowupNotification(notification, null)) {
    return `/clients/view-client-detail/${clientId}`;
  }

  if ([2, 3, 5, 7].includes(type) && appId) {
    return appType === 1
      ? `/applications/view-migration-application-detail/${appId}`
      : `/applications/view-education-application-detail/${appId}`;
  }

  if ([4, 15].includes(type)) {
    if (appType === 1 && appId) {
      return `/applications/view-migration-application-detail/${appId}`;
    }
    if (appType === 2 && appId) {
      return `/applications/view-education-application-detail/${appId}`;
    }
    if (clientId) {
      return `/clients/view-client-detail/${clientId}`;
    }
  }

  if (type === 9 && appType !== 9 && clientId) {
    return `/providers/provider-details/${clientId}`;
  }
  if (type === 9 && appType === 9 && appId) {
    return `/providers/provider-details/${appId}`;
  }

  if (type === 0 && appType === 10) {
    return "/appointments/view-appointments";
  }
  if (type === 0 && appType === 4 && clientId) {
    return `/clients/view-client-detail/${clientId}`;
  }

  if (type === 12 && clientId) {
    return `/agents/view-agent/${clientId}/detail`;
  }

  return null;
}

function labelForRoute(route, notification) {
  if (!route) return "Open";
  if (route.includes("/edit-invoice/")) return "Open invoice";
  if (route.includes("/view-education-application-detail/")) return "Open application";
  if (route.includes("/view-migration-application-detail/")) return "Open application";
  if (route.includes("/view-client-detail/")) return "Open client";
  if (route.includes("/provider-details/")) return "Open provider";
  if (route.includes("/view-appointments")) return "Open appointments";
  if (route.includes("/view-agent/")) return "Open agent";
  if (isInvoiceFollowupNotification(notification, null)) return "Open invoice";
  return "Open in app";
}

/**
 * In-app path for header / view-all notification links.
 */
export function resolveNotificationAppRoute(notification) {
  if (!notification) return null;

  const payload = parseNotificationPayload(notification.payload);
  if (payload?.route) return payload.route;

  const invoiceRoute = resolveInvoiceFollowupRoute(notification, payload);
  if (invoiceRoute) return invoiceRoute;

  return resolveLegacyNotificationRoute(notification);
}

export function resolveNotificationAction(notification) {
  const to = resolveNotificationAppRoute(notification);
  if (!to) return null;
  return {
    to,
    label: labelForRoute(to, notification),
  };
}
